import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styled from '@emotion/styled';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import * as AppActions from '../../stores/app/actions';
import LoadingProgress from '../loading/index.jsx';
import {
  STATUS_PROCESS_OBV,
  STATUS_PROGRESS_LOADING
} from '../../constants/status';
import { Modal } from '../modal/index.jsx';
import ErrorIcon from '../../assets/error-icon.svg';
import PlaidLink from '../plaidLink/index.jsx';
import PropTypes from 'prop-types';
import { customDesktop, customMobile } from '../../globalStyleds';
import { useNavigate } from 'react-router-dom';
import {
  isPlaidExist,
  setPlaidFrameZIndex,
  getDeviceInfo
} from "../../utils/common";
import { getTransactionInfo } from '../../utils/sessionStorage';
import {useMappingErrorMessage} from "../../hooks/useMappingErrorMessage";
import { ClientJS } from 'clientjs';
import { useTranslation } from 'react-i18next';

const ConnectBankStyled = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  ${(props) => (props.customStyle.isMobile ? customMobile : customDesktop)}
`;


const ConnectBank = ({ code = null, afterOnSuccess = () => null, oauthPresent = null,
                       onClickBack = null,
                       isMobile = false, merchantReturnUrl = null, theme = 'light', setStatusLoadingFinish = null, isResize }) => {
  const {t} = useTranslation();                      
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { token, receivedRedirectUri } = getTransactionInfo() || {};
  const error = useMappingErrorMessage();
  
  // Create loading messages array from i18n
  const loadingMessages = [
    t('loading.message.processing'),
    t('loading.message.verifying')
  ];
  
  const [activeLoading, setActiveLoading] = useState(false);
  const [isExitPlaid, exitPlaid] = useState(false);
  const [statusProgress, setStatusProgress] = useState(STATUS_PROGRESS_LOADING.START);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isPlaidLoadingFinish, setPlaidLoadingFinish] = useState(isPlaidExist());
  const appStore = useSelector((state) => state, shallowEqual).app;
  const { obvInfo, statusProcess, isLoading, isVerification } = appStore;
  const obvInitiated = useRef(false);
  const plaidLinkCreated = useRef(false);

  useEffect(() => {
    const onPlaidFrameFound = () => {
      if (isPlaidExist()) {
        setPlaidFrameZIndex();
        setPlaidLoadingFinish(true);
      }
    };
    // The iframe can already be there when this effect runs (child effects run first).
    onPlaidFrameFound();
    const observer = new MutationObserver(onPlaidFrameFound);
    observer.observe(document, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const createSectionStep = useCallback((status) => {
      if (status === STATUS_PROCESS_OBV.NONE && !obvInitiated.current) {
        obvInitiated.current = true;
        return dispatch(AppActions.createOBV({code, merchantReturnUrl, theme}));
      } else if (status === STATUS_PROCESS_OBV.INIT && !plaidLinkCreated.current) {
        plaidLinkCreated.current = true;
        const deviceData = getDeviceInfo(new ClientJS());
        return dispatch(AppActions.createPlaidLink(merchantReturnUrl, deviceData));
      } else {
        return true;
      }
    },
    [dispatch, code, merchantReturnUrl, obvInitiated, plaidLinkCreated, theme],
  );

  useEffect(() => {
    if (code) createSectionStep(statusProcess);
  }, [statusProcess, createSectionStep, code]);

  useEffect(() => {
    if (error?.message) {
      setIsModalVisible(true);
      setPlaidLoadingFinish(true);
      setActiveLoading(false);
    }
  }, [error]);

  useEffect(() => {
    if (isLoading) {
      setActiveLoading(true);
      setStatusProgress(STATUS_PROGRESS_LOADING.START);
    } else {
      setStatusProgress(STATUS_PROGRESS_LOADING.FINISH);
    }
    return () => {
      setStatusProgress(STATUS_PROGRESS_LOADING.NONE);
    };
  }, [isLoading]);

  useEffect(() => {
    if (isVerification) {
      exitPlaid(true);
      afterOnSuccess();
    }
  }, [dispatch, isVerification, afterOnSuccess]);

  useEffect(() => {
    setStatusLoadingFinish(!activeLoading && isPlaidLoadingFinish)
  }, [setStatusLoadingFinish, activeLoading, isPlaidLoadingFinish])

  const onSuccess = useCallback(
    (token, metadata) => {
      // Link is finished once it hands back the public token. Opening it again would replay the
      // consumed oauth_state_id (OAUTH_STATE_ID_ALREADY_PROCESSED) and its exit would fail the
      // transaction, so it stays closed even if the loading timer resets activeLoading meanwhile.
      exitPlaid(true);
      setPlaidLoadingFinish(true);
      dispatch(AppActions.obvVerification(metadata));
    },
    [dispatch],
  );

  const onEvent = useCallback(
    (eventName, metadata) => {
      if (metadata.view_name) dispatch(AppActions.createPlaidEvent(eventName, metadata));
      if (eventName === 'OPEN') setPlaidLoadingFinish(true);
    },
    [dispatch],
  );

  const onExit = useCallback((error, metadata) => {
    if (metadata && typeof metadata === "object") {
      dispatch(AppActions.exitEvent({error, metadata}));
    }
  }, [dispatch]);


  const linkToken = useMemo(() => {
    if(obvInfo?.token) {
      return obvInfo?.token;
    }
    if(token) {
      return token;
    }
    return obvInfo?.token || (!code && token);
  }, [token, obvInfo?.token, code]);

  useEffect(() => {
    if (oauthPresent && !linkToken && !receivedRedirectUri) {
      const params = {
        oauth_state_id: oauthPresent,
        receivedRedirectUri: window.location.href
      };
      navigate(`/oauth-callback?${new URLSearchParams(params).toString()}`);
    }
  }, [navigate, linkToken, oauthPresent, receivedRedirectUri])

  return (
    <>
      {linkToken && !activeLoading && !isExitPlaid && !error && (
        <PlaidLink
          oauthPresent={oauthPresent}
          token={linkToken}
          receivedRedirectUri={receivedRedirectUri}
          onSuccess={onSuccess}
          onEvent={onEvent}
          onExit={onExit}
          isEmbedded={isMobile}
        />
      )}
      <ConnectBankStyled customStyle={{ isMobile: isMobile || isResize }}>
        <div className='box-extend'>
          <Modal
            message={error?.message}
            subMessage={error?.subMessage}
            description={error?.description}
            errorUrl={ErrorIcon}
            isFullScreen={true}
            visible={isModalVisible}
            onClickBack={onClickBack}
          />
          <LoadingProgress
            active={!isPlaidLoadingFinish || activeLoading || isLoading}
            messages={loadingMessages}
            timeNextMessage={3000}
            timeBackgroundRun={2000}
            progress={statusProgress}
            afterFinish={() => {
              setActiveLoading(false);
              setStatusProgress(STATUS_PROGRESS_LOADING.NONE);
            }}
          />
        </div>
      </ConnectBankStyled>
    </>
  );
};

ConnectBank.propTypes = {
  code: PropTypes.string,
  afterOnSuccess: PropTypes.func,
  oauthPresent: PropTypes.string,
  onClickBack: PropTypes.func,
  isMobile: PropTypes.bool,
  merchantReturnUrl: PropTypes.string,
  theme: PropTypes.string,
  setStatusLoadingFinish: PropTypes.func,
  isResize: PropTypes.bool
};

export default ConnectBank;
